import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Circle, CalendarDays, FileText } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";
import { StatusBadge, DemoRibbon } from "../components/StatusBadge";

export default function GminaPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/gminas/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [id]);

  if (data === null) return <div className="p-20 text-center text-slate-500">{t("common.loading")}</div>;
  if (data === false) return <div className="p-20 text-center text-slate-500">404</div>;

  const { gmina, documents, consultations, parcels } = data;

  return (
    <div data-testid="gmina-page" className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">{gmina.name}</h1>
            <DemoRibbon label={t("demo.label")} />
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {t("common.powiat")} {gmina.powiat} · {t("common.woj")} {gmina.wojewodztwo} · {gmina.type}
          </p>
        </div>
        <StatusBadge status={gmina.plan_ogolny_status} label={t(`status.${gmina.plan_ogolny_status}`)} testId="gmina-status-badge" />
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("parcel.planOgolny")}</p>
          <div className="mt-2"><StatusBadge status={gmina.plan_ogolny_status} label={t(`status.${gmina.plan_ogolny_status}`)} /></div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("gmina.coverage")}</p>
          <p className="mt-1 font-heading text-2xl font-extrabold">{gmina.mpzp_coverage_pct}%</p>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-[#DC143C]" style={{ width: `${gmina.mpzp_coverage_pct}%` }} />
          </div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("report.effective")}</p>
          <p className="mt-1 font-mono-data text-lg font-semibold">{gmina.latest_document_date || "—"}</p>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border bg-white p-6">
          <h2 className="font-heading text-lg font-semibold">{t("gmina.timeline")}</h2>
          {gmina.timeline.length === 0 && <p className="mt-3 text-sm text-slate-500">—</p>}
          <ol className="mt-4 space-y-4 border-l-2 border-slate-200 pl-5">
            {gmina.timeline.map((ev, i) => (
              <li key={i} data-testid={`timeline-event-${i}`} className="relative">
                <span className="absolute -left-[27px] top-0.5">
                  {ev.status === "done"
                    ? <CheckCircle2 size={16} className="text-emerald-600" />
                    : <Circle size={16} className="text-amber-500" />}
                </span>
                <p className="font-mono-data text-xs text-slate-400">{ev.date}</p>
                <p className="text-sm font-medium text-slate-800">{lang === "pl" ? ev.title_pl : ev.title_en}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="rounded-xl border bg-white p-6">
          <h2 className="font-heading text-lg font-semibold">{t("parcel.consultations")}</h2>
          <div className="mt-4 space-y-3">
            {consultations.length === 0 && <p className="text-sm text-slate-500">—</p>}
            {consultations.map((c) => (
              <div key={c.id} data-testid={`gmina-consultation-${c.id}`} className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">{c.document_title}</p>
                  <p className="flex items-center gap-1 text-xs text-slate-500">
                    <CalendarDays size={11} /> {c.start_date} → {c.end_date}
                  </p>
                </div>
                <StatusBadge status={c.status} label={t(`cons.${c.status}`)} />
              </div>
            ))}
          </div>

          <h2 className="mt-8 font-heading text-lg font-semibold">{t("gmina.documents")}</h2>
          <div className="mt-4 space-y-2">
            {documents.length === 0 && <p className="text-sm text-slate-500">—</p>}
            {documents.map((d) => (
              <div key={d.id} data-testid={`gmina-document-${d.id}`} className="flex items-start gap-3 rounded-lg border px-4 py-3">
                <FileText size={16} className="mt-0.5 shrink-0 text-slate-400" />
                <div>
                  <p className="text-sm font-medium">{d.title}</p>
                  <p className="text-xs text-slate-500">{d.authority} · {d.publication_date}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {parcels.length > 0 && (
        <section className="mt-8 rounded-xl border bg-white p-6">
          <h2 className="font-heading text-lg font-semibold">{t("gmina.parcels")}</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {parcels.map((p) => (
              <button
                key={p.id}
                data-testid={`gmina-parcel-${p.id}`}
                onClick={() => navigate(`/dzialka/${p.id}`)}
                className="rounded-lg border p-4 text-left transition-colors hover:border-[#DC143C]/50 hover:bg-[#FEF2F2]/40"
              >
                <p className="font-mono-data text-sm font-semibold">{p.full_number}</p>
                <p className="mt-1 text-xs text-slate-500">{p.address}</p>
                <div className="mt-2"><StatusBadge status={p.plan_ogolny_status} label={t(`status.${p.plan_ogolny_status}`)} /></div>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
